import {Button} from '@ui/shadcn/button';
import {isChunkLoadError} from '@lib/chunk-reload';

interface ErrorFallbackProps {
  error: unknown;
  /** Стек компонентов из `componentDidCatch` — для блока «Детали ошибки». */
  componentStack?: string | null;
}

/**
 * Экран «Что-то пошло не так» с деталями и кнопкой перезагрузки. Общий для
 * `ErrorBoundary` (ошибки вне роутера) и `RouteErrorPage` (ошибки страниц).
 * Для ошибки загрузки чанка — отдельный текст: сайт обновился или оборвалось
 * соединение, лечится перезагрузкой.
 */
export const ErrorFallback = ({error, componentStack}: ErrorFallbackProps) => {
  const chunkError = isChunkLoadError(error);
  return (
    <div className='min-h-screen flex items-center justify-center p-6 bg-[var(--color-background)]'>
      <div className='max-w-[640px] w-full bg-[var(--color-surface)] rounded-xl border border-[var(--score-bad)]/30 shadow-sm p-6'>
        <h2 className='text-base font-semibold text-[var(--score-bad)] mb-2'>
          {chunkError ? 'Не удалось загрузить страницу' : 'Что-то пошло не так'}
        </h2>
        <p className='text-xs text-[var(--color-muted-foreground)] mb-4 leading-relaxed'>
          {chunkError
            ? 'Сайт обновился, пока вкладка была открыта, или прервалось соединение. Перезагрузите страницу.'
            : 'Произошла ошибка при отображении страницы. Попробуйте перезагрузить вкладку или вернитесь на главную.'}
        </p>
        <details className='text-[11px] text-[var(--color-foreground)] leading-relaxed bg-[var(--color-muted)] rounded-lg p-3 mb-4 whitespace-pre-wrap break-words'>
          <summary className='cursor-pointer text-[var(--color-secondary)] font-medium mb-2'>
            Детали ошибки
          </summary>
          {error instanceof Error ? error.toString() : String(error)}
          {componentStack ? `\n${componentStack}` : ''}
        </details>
        <Button
          type='button'
          size='sm'
          onClick={() => window.location.reload()}
        >
          Перезагрузить страницу
        </Button>
      </div>
    </div>
  );
};
